import React, { useState } from 'react';
import { ArrowLeft, Users, Shield, Building } from 'lucide-react';
import { Button, Card } from '../UIComponents';
import { UserManagement } from './UserManagement';

type AdminTab = 'users' | 'roles' | 'access';

interface AdminPanelProps {
  onBack: () => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  const tabs = [
    { id: 'users' as const, label: 'Users', icon: Users },
    { id: 'roles' as const, label: 'Roles & Access', icon: Shield },
    { id: 'access' as const, label: 'Customer Access', icon: Building },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
              Administration
            </h1>
            <p className="text-slate-500 mt-1">Manage users, roles, and permissions</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Card className="p-2">
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-accent text-white shadow-lg shadow-accent/25'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === 'users' && <UserManagement />}
      {activeTab === 'roles' && (
        <Card>
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Role Management</h3>
            <p className="text-slate-500 text-sm">
              Roles are pre-configured. Use the Users tab to assign roles.
            </p>
            <div className="mt-8 max-w-md mx-auto space-y-3">
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                <span className="font-semibold text-purple-700">Superuser</span>
                <span className="text-sm text-purple-600">Full portal access</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                <span className="font-semibold text-blue-700">Administrator</span>
                <span className="text-sm text-blue-600">Full access to assigned customers</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <span className="font-semibold text-emerald-700">Customer Manager</span>
                <span className="text-sm text-emerald-600">Limited customer management</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                <span className="font-semibold text-slate-700">User</span>
                <span className="text-sm text-slate-600">Read-only access</span>
              </div>
            </div>
          </div>
        </Card>
      )}
      {activeTab === 'access' && (
        <Card>
          <div className="text-center py-12">
            <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Customer Access Overview</h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Use the Users tab to manage individual user access to customers.
              Click on a user to configure their customer permissions.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};
